import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import dayjs from "dayjs";
import { useMemo } from "react";
import styled from "styled-components";
import {
  type CalendarDayUser,
  type CalendarShift,
  type CalendarTimeOff,
  useCalendarStore,
} from "../../store/useCalendarStore";
import { isInPast } from "../../utils";
import { spansDate } from "../hooks/dayAbsence";
import useDrag from "../hooks/useDrag";
import AppointmentsColumn from "./AppointmentsColumn";
import ShiftBlock from "./ShiftBlock";
import { SlotHoverBand } from "./SlotHoverBand";
import { TimeoffColumn } from "./TimeoffColumn";
import { useTheme } from "next-themes";

const UserColumn = ({
  user,
  date,
  isLastChild,
  timeoffs,
  usersInOrder,
}: {
  user: CalendarDayUser;
  date: string;
  isLastChild: boolean;
  timeoffs: CalendarTimeOff[];
  usersInOrder: number[];
}) => {
  const { theme, resolvedTheme } = useTheme();
  const activeTheme = theme === "system" ? resolvedTheme : theme;

  const {
    setSelectedAppointmentId,
    setLastSelectedUserId,
    setLastSelectedTime,
    setLastSelectedDate,
    setLastSelectedResourceId,
    setNewBreakData,
  } = useCalendarStore((state) => state);

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const { calendarHeight, slotHeight, allShifts } = useCalendarStore(
    (state) => state.calendarData,
  );

  const shifts = allShifts?.filter(
    (shift: CalendarShift) =>
      shift.userId === user.userId && spansDate(shift.start, shift.end, date),
  );

  const isDateInPast = useMemo(() => isInPast(dayjs(date).toDate()), [date]);

  const { calculateTimeForYOffset } = useDrag();

  return (
    <Wrapper className={"userColumn"}>
      <EventColumn
        isLastChild={isLastChild}
        className="eventColumn"
        data-identifier="calendar-user-column"
        isInPast={isDateInPast}
        slotHeight={slotHeight}
        height={calendarHeight + 1}
        permission={permissions.manage_appointments}
        onClick={(e) => {
          e.stopPropagation();
          setLastSelectedUserId(user.userId);
          setLastSelectedResourceId(null);
          setSelectedAppointmentId(-1);

          const { currentTarget } = e;
          const rect = currentTarget.getBoundingClientRect();
          const yOffset = e.clientY - rect.top;

          const time = calculateTimeForYOffset(yOffset);
          const [hour, minute] = time.split(":");
          const dateTime = dayjs(date)
            .set("hour", Number(hour))
            .set("minute", Number(minute))
            .format("YYYY-MM-DDTHH:mm");

          setLastSelectedTime(dateTime);
          setLastSelectedDate(date);

          setNewBreakData({
            userId: user.userId,
            date: date,
            startTime: time,
          });
        }}
      >
        <SlotHoverBand />
        <AppointmentsColumn
          date={date}
          userId={user.userId}
          usersInOrder={usersInOrder}
        ></AppointmentsColumn>

        {shifts?.map((shift: CalendarShift) => (
          <ShiftBlock shift={shift} date={date} key={shift.start}></ShiftBlock>
        ))}

        {timeoffs && (
          <TimeoffColumn
            timeoffs={timeoffs}
            date={date}
            shifts={shifts}
          ></TimeoffColumn>
        )}
        <InPastOverlay
          slotHeight={slotHeight}
          isInPast={isDateInPast}
          isDarkTheme={activeTheme === "dark"}
        ></InPastOverlay>
      </EventColumn>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex: 1;
  position: relative;

  min-width: 80px;
`;

const InPastOverlay = styled.div<{
  isDarkTheme: boolean;
  slotHeight: number;
  isInPast: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(to right, transparent 1px, transparent 1px),
    linear-gradient(
      ${(props) => (props.isDarkTheme ? "#1e1e1e" : "#fafafa")} 1px,
      ${(props) => (props.isDarkTheme ? "#1e1e1e" : "#fafafa")} 1px
    );
  background-size: 100% ${(props) => props.slotHeight}px;

  pointer-events: none;
  opacity: ${(props) => (props.isInPast ? 1 : 0)};

  z-index: -4;
`;

const EventColumn = styled.div<{
  height: number;
  permission: boolean;
  isLastChild: boolean;
  slotHeight: number;
  isInPast: boolean;
}>`
  height: ${(props) => props.height}px;
  width: 100%;
  cursor: ${(props) => (props.permission ? "pointer" : "default")};

  position: relative;
  border-right: ${(props) =>
    !props.isLastChild ? "2px dashed #EAEBED" : "none"};
  background-image:
    linear-gradient(to right, transparent 0px, transparent 1px),
    linear-gradient(lightgrey 1px, transparent 1px);
  background-size: 100% ${(props) => props.slotHeight}px;
`;

export default UserColumn;
